import { useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import {
  Logo, ArrowRight, FileText, Calendar, Heart, GraduationCap, Users, Stethoscope, Brain, Shield, Eye, Sparkles, Phone, BarChart, X, Check
} from '../components/Icons.jsx';
import FounderNote from '../components/FounderNote.jsx';
import BursaryCheck from '../components/BursaryCheck.jsx';
import { useAuth } from '../lib/auth.jsx';
import { useProfile } from '../lib/useProfile.js';

const tabs = [
  { id: 'overview', label: 'Overview' },
  { id: 'child', label: 'My child' },
  { id: 'resources', label: 'Resources & timeline' },
  { id: 'questions', label: 'Common questions' }
];

export default function Parents() {
  const [params, setParams] = useSearchParams();
  const tab = params.get('tab') || 'overview';
  const { user } = useAuth();
  const { profile, copy } = useProfile();
  const setTab = (id) => setParams(id === 'overview' ? {} : { tab: id }, { replace: true });

  return (
    <div style={{ background: 'var(--bg)', minHeight: '100vh' }}>
      <header style={{
        background: '#fff', borderBottom: '1px solid var(--border)',
        padding: '14px 24px', display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        position: 'sticky', top: 0, zIndex: 10
      }}>
        <Link to="/parents" style={{ display: 'flex', alignItems: 'center', gap: 10, textDecoration: 'none' }}>
          <Logo width="36" height="36" onLight />
          <div style={{ display: 'flex', flexDirection: 'column', lineHeight: 1, fontFamily: 'Montserrat, sans-serif' }}>
            <span style={{ fontSize: 22, fontWeight: 800, letterSpacing: '0.04em', textTransform: 'uppercase', color: 'var(--brand-deep)' }}>Medi</span>
            <span style={{ fontSize: 9, fontWeight: 600, letterSpacing: '0.18em', textTransform: 'uppercase', color: 'var(--text-muted)', marginTop: 4 }}>For parents · powered by Medic Mind</span>
          </div>
        </Link>
        <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
          {user ? (
            <>
              <span style={{ fontSize: 13, color: 'var(--text-muted)' }}>Signed in as {user.email}</span>
              <Link to="/" className="btn btn-secondary">Open portal →</Link>
            </>
          ) : (
            <>
              <Link to="/login" className="btn btn-secondary" style={{ textDecoration: 'none' }}>Sign in</Link>
              <Link to="/signup?role=parent" className="btn btn-primary" style={{ textDecoration: 'none' }}>Create parent account →</Link>
            </>
          )}
        </div>
      </header>

      <main className="main" style={{ maxWidth: 1100, margin: '0 auto' }}>
        <div style={{ marginBottom: 24 }}>
          <div className="welcome-kicker">For parents</div>
          <h1 className="welcome-h1">{user?.email ? `Hi ${(user.email || '').split('@')[0]} 👋` : 'Helping you help them.'}</h1>
          <p className="welcome-sub">Plain-English guides, free tools, and a tutor bench — without the panic.</p>
        </div>

        <TabBar tabs={tabs} active={tab} onChange={setTab} />

        {tab === 'overview' && <Overview profile={profile} />}
        {tab === 'child' && <MyChild profile={profile} />}
        {tab === 'resources' && <Resources />}
        {tab === 'questions' && <Questions />}

        <footer style={{ textAlign: 'center', padding: '40px 0 12px', color: 'var(--text-muted)', fontSize: 13 }}>
          © {new Date().getFullYear()} Medic Mind. UCAT is a registered trademark of UCAT Consortium. We are not affiliated with the consortium.
        </footer>
      </main>
    </div>
  );
}

function TabBar({ tabs, active, onChange }) {
  return (
    <nav style={{ display: 'flex', gap: 4, padding: 4, marginBottom: 22, background: 'var(--card-soft)', borderRadius: 12, overflowX: 'auto', WebkitOverflowScrolling: 'touch' }}>
      {tabs.map((t) => (
        <button
          key={t.id} onClick={() => onChange(t.id)}
          style={{
            padding: '8px 14px', fontSize: 13.5, fontWeight: 600, cursor: 'pointer',
            background: active === t.id ? '#fff' : 'transparent',
            color: active === t.id ? 'var(--brand-deep)' : 'var(--text-muted)',
            border: 'none', borderRadius: 8, fontFamily: 'inherit', whiteSpace: 'nowrap',
            boxShadow: active === t.id ? 'var(--shadow-card)' : 'none'
          }}
        >{t.label}</button>
      ))}
    </nav>
  );
}

function Overview() {
  return (
    <>
      <FounderNote
        kicker="A note from"
        title="To the parents trying to help."
        ctaText="Read the full letter"
        ctaTo="/about"
      >
        <p style={{ marginTop: 0 }}>If you're reading this, your child is applying to medicine or dentistry — and you're trying to work out how much to help, how much to back off, and whether they need a tutor.</p>
        <p>I've sat where they're sitting. I almost gave up on the UCAT in Year 13 — overthought every question, ran out of time on every mock. What changed wasn't talent. It was the <em>process</em>. By exam day I scored 100% in UCAT.</p>
        <p>Ten years and 60,000+ applicants later: the gap between the kids who get offers and the ones who don't is almost never raw ability. It's whether they trained the right reflexes, in the right order, for long enough.</p>
        <p>This portal is everything I wish my own parents had. If you want a human in the loop, every Medic Mind tutor was personally trained by me — their students' average UCAT lands in the top 30% nationally.</p>
        <p>Worried? Book the free 15-min call. We'll give you an honest read. No pitch.</p>
      </FounderNote>

      <section className="r-grid-3" style={{ marginBottom: 22 }}>
        <Metric value="15 Oct" label="UCAS deadline for medicine and dentistry" />
        <Metric value="2400+" label="UCAT score most offer-holders aim for" />
        <Metric value="Top 30%" label="UCAT average of Medic Mind tutees nationally" />
      </section>

      <h2 style={{ margin: '0 0 14px', fontSize: 22 }}>The essentials</h2>
      <section className="parents-grid" style={{ marginBottom: 28 }}>
        <ParentCard icon={<FileText />} title="What the UCAT is" body="A 2-hour test. Three reasoning sections (VR, DM, QR) scored 300–900 each — max total 2700. SJT scored in bands 1–4." />
        <ParentCard icon={<Calendar />} title="The dates that matter" body="UCAT July–September. UCAS deadline 15 October. Interviews November–March. Offers spring." />
        <ParentCard icon={<Heart />} title="How to actually help" body="Avoid 'have you revised today?' — it backfires. Help with logistics, protect sleep, let them lead. Cook dinner. Be present." />
        <ParentCard icon={<Brain />} title="When a tutor genuinely helps" body="Mid-prep plateaus, persistent gap in one section, or UCAT date < 6 weeks out with no plan." />
        <ParentCard icon={<Shield />} title="The SJT myth" body="The section parents underestimate most. SJT in bands 3–4 disqualifies at strict schools." />
        <ParentCard icon={<Stethoscope />} title="Work experience" body="Quality over quantity. One Career Camp week + ongoing volunteering beats six shadowing days." />
      </section>

      <Backfire />
      <BursaryCheck />
    </>
  );
}

function MyChild({ profile }) {
  const hasUcatDate = !!profile?.ucatDate;
  const target = profile?.targetScore;
  const discipline = profile?.discipline;
  return (
    <>
      <section className="card" style={{ marginBottom: 22, padding: '24px 26px' }}>
        <h2 style={{ margin: '0 0 6px', fontSize: 20 }}>Your child's profile</h2>
        <p style={{ color: 'var(--text-muted)', margin: '0 0 18px', fontSize: 14 }}>
          {profile && Object.keys(profile).length > 0
            ? 'Pulled from the onboarding wizard. Want to update it? Use Switch role in the student app.'
            : 'Your child hasn\'t completed onboarding yet. Have them open the student app and run through the wizard.'}
        </p>

        <div className="r-grid-3" style={{ marginBottom: 8 }}>
          <Metric value={discipline ? (discipline === 'medicine' ? 'Medicine' : 'Dentistry') : '—'} label="Application track" />
          <Metric value={hasUcatDate ? formatDate(profile.ucatDate) : 'Not set'} label="UCAT date" />
          <Metric value={target ? target : 'Not set'} label="Target UCAT (VR + DM + QR)" />
        </div>
      </section>

      <section className="card" style={{ marginBottom: 22, padding: '24px 26px', background: 'linear-gradient(135deg, var(--accent-soft), #fff)' }}>
        <h3 style={{ margin: '0 0 6px', fontSize: 17 }}>Connect a child to track their drill progress</h3>
        <p style={{ margin: '0 0 14px', color: 'var(--text-2)', fontSize: 14 }}>This part is being built. For now, sit down with them and run the Strategy Audit together — it gives you the same information a connected dashboard will surface automatically.</p>
        <Link to="/strategy-audit" className="btn btn-primary">Run the strategy audit <ArrowRight width="14" height="14" /></Link>
      </section>

      <Backfire />
    </>
  );
}

function Resources() {
  return (
    <>
      <h2 style={{ margin: '0 0 14px', fontSize: 22 }}>Free tools you can open with your child</h2>
      <p style={{ color: 'var(--text-muted)', margin: '0 0 16px', maxWidth: 720 }}>Sit down together — each one is under 10 minutes and gives you something concrete to talk about.</p>
      <section className="parents-grid" style={{ marginBottom: 30 }}>
        <ResourceCard to="/grade-check" icon={<GraduationCap />} title="Grade realism check" body="A-level / IB predictions + UCAT → which of the 28 UK med schools meet their bar." />
        <ResourceCard to="/where-can-i-apply" icon={<BarChart />} title="Where can I apply?" body="Filter all 28 UK med schools by UCAT score." />
        <ResourceCard to="/career-camp" icon={<Stethoscope />} title="Career Camp" body="Real NHS hospital + hospice placements. Bursaries up to 100%." />
        <ResourceCard to="/timeline" icon={<Calendar />} title="Application timeline" body="GCSEs to results day. Print it for the fridge." />
        <ResourceCard to="/videos" icon={<Eye />} title="Free UCAT videos" body="17 short Medic Mind teaching videos — VR, DM, QR, SJT." />
        <ResourceCard to="/medi" icon={<Sparkles />} title="Ash — AI tutor" body="Free 20 messages a day. Your child can ask anything." />
      </section>

      <Bursary />

      <section className="card" style={{ marginBottom: 22 }}>
        <h3 style={{ margin: '0 0 8px', fontSize: 18 }}>The application timeline at a glance</h3>
        <div className="timeline" style={{ marginTop: 14 }}>
          <TimelineRow when="Year 12 — Spring" what="Start work experience" body="One care-setting + one clinical placement, with a reflection log." />
          <TimelineRow when="Year 12 — Summer" what="Career Camp + light UCAT" body="The highest-leverage single week of the application." />
          <TimelineRow when="Year 13 — Jun–Aug" what="Real UCAT prep window" body="6–10 weeks of structured prep. Mocks in the second half." />
          <TimelineRow when="Year 13 — Jul–Sep" what="Sit the UCAT" body="Book the date in May when bookings open. Avoid late September." />
          <TimelineRow when="Year 13 — Sep" what="Finalise UCAS" body="Personal statement edited 5+ times. Four medical-school choices picked using UCAT score." />
          <TimelineRow when="Year 13 — Oct 15" what="UCAS deadline" body="Hard deadline. Run a school-internal 1 Oct cutoff to buffer it." />
          <TimelineRow when="Year 13 — Nov–Mar" what="Interviews" body="MMI dominates. Mock interviews matter as much as content." />
        </div>
      </section>
    </>
  );
}

function Questions() {
  const items = [
    { q: 'How much does all this cost?', a: "The portal you're looking at is free. The 15-min advisor call is free. Career Camp is roughly £500–£900 with bursaries 50–100% for eligible families. 1-to-1 tutoring is £50–£90/hour — most students need 6–12 hours total." },
    { q: 'When should we start?', a: "Year 11 is too early for UCAT — focus on strong GCSEs. Year 12 spring is when work experience and Chemistry A-level matter most. Real UCAT prep starts the summer between Year 12 and Year 13." },
    { q: "What if our school doesn't help?", a: "Many don't. The free tools here cover the gaps — Grade check, Where Can I Apply, the timeline, MMI prep videos. The 15-min call is also free and was built for exactly this case." },
    { q: 'They scored badly on a mock. Are they doomed?', a: "Almost certainly not. Mock scores swing massively with prep. Trajectory across 4–6 mocks matters; one bad mock means almost nothing." },
    { q: "What if they don't get in this cycle?", a: "Around 1 in 5 applicants doesn't get an offer first time. Three viable routes: re-apply (with stronger UCAT + extra work experience), graduate-entry medicine via GAMSAT, or study abroad. We can walk through which fits your family best in the free call." }
  ];
  return (
    <>
      <h2 style={{ margin: '0 0 14px', fontSize: 22 }}>The questions parents ask most</h2>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginBottom: 32 }}>
        {items.map((f) => (
          <details key={f.q} className="card" style={{ padding: 0 }}>
            <summary style={{ padding: '14px 18px', cursor: 'pointer', fontSize: 15, fontWeight: 600, listStyle: 'none', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              {f.q}<span style={{ fontSize: 18, color: 'var(--text-muted)', flexShrink: 0, marginLeft: 12 }}>+</span>
            </summary>
            <div style={{ padding: '0 18px 16px', color: 'var(--text-2)', fontSize: 14, lineHeight: 1.6 }}>{f.a}</div>
          </details>
        ))}
      </div>

      <section className="parents-cta">
        <div>
          <h2>Talk to a Medic Mind advisor</h2>
          <p>Free 15-min call. Honest read on your child's plan. No pressure.</p>
        </div>
        <a className="btn" href="https://www.medicmind.co.uk/contact-us/" target="_blank" rel="noreferrer">Book free call <ArrowRight width="16" height="16" /></a>
      </section>
    </>
  );
}

function Backfire() {
  return (
    <section className="card" style={{ marginBottom: 24, padding: '22px 24px', background: '#fef3c7', border: '1px solid #fde68a' }}>
      <h2 style={{ margin: '0 0 6px', fontSize: 20 }}>What backfires (no judgement)</h2>
      <p style={{ color: 'var(--text-2)', margin: '0 0 14px' }}>From 10 years of advising applicants and their parents.</p>
      <div className="r-grid-2" style={{ gap: 14 }}>
        <BackfireRow bad={`"Have you done UCAT today?" daily`} good="Block one no-questions revision window in the family calendar. Then drop it." />
        <BackfireRow bad="Comparing them to a friend's child who got in" good="Use Grade Check — it's apolitical." />
        <BackfireRow bad={`Rebooking the UCAT to a "better day" last-minute`} good="Stick with the booked date, protect the week before." />
        <BackfireRow bad="Writing parts of the personal statement" good="Admissions tutors spot it. Help with structure; let them write the words." />
        <BackfireRow bad={`Suggesting they "just apply to dentistry too"`} good="Real preference fine; panic pivot isn't." />
        <BackfireRow bad="Treating a UCAT mock score as predictive" good="Trajectory across 4–6 mocks beats any single score." />
      </div>
    </section>
  );
}


function Metric({ value, label }) {
  return (
    <div className="card" style={{ borderLeft: '4px solid var(--brand-purple)' }}>
      <div style={{ fontSize: 'clamp(22px, 3vw, 28px)', fontWeight: 800, fontFamily: 'Montserrat, sans-serif', color: 'var(--brand-deep)', letterSpacing: '-0.01em' }}>{value}</div>
      <p style={{ margin: '4px 0 0', fontSize: 12.5, color: 'var(--text-2)' }}>{label}</p>
    </div>
  );
}
function ParentCard({ icon, title, body }) {
  return <div className="parents-card"><div className="icon-wrap">{icon}</div><h3>{title}</h3><p>{body}</p></div>;
}
function ResourceCard({ to, icon, title, body }) {
  return (
    <Link to={to} className="parents-card" style={{ textDecoration: 'none', color: 'inherit', cursor: 'pointer' }}>
      <div className="icon-wrap">{icon}</div>
      <h3>{title}</h3>
      <p>{body}</p>
      <span style={{ marginTop: 10, color: 'var(--accent)', fontSize: 13, fontWeight: 600, display: 'inline-flex', alignItems: 'center', gap: 6 }}>Open <ArrowRight width="14" height="14" /></span>
    </Link>
  );
}
function BackfireRow({ bad, good }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
      <div style={{ display: 'flex', gap: 8, alignItems: 'flex-start' }}><X width="16" height="16" style={{ color: '#b91c1c', flexShrink: 0, marginTop: 2 }} /><span style={{ fontSize: 14, fontWeight: 600 }}>{bad}</span></div>
      <div style={{ display: 'flex', gap: 8, alignItems: 'flex-start' }}><Check width="16" height="16" style={{ color: 'var(--live-green)', flexShrink: 0, marginTop: 2 }} /><span style={{ fontSize: 14, color: 'var(--text-2)', lineHeight: 1.5 }}>{good}</span></div>
    </div>
  );
}
function TimelineRow({ when, what, body }) {
  return <div className="timeline-row"><div className="when">{when}</div><div className="what"><h4>{what}</h4><p>{body}</p></div></div>;
}
function formatDate(iso) {
  try { return new Date(iso).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' }); }
  catch { return iso; }
}
